// このストアはモーダル各ステップで必要となるマスターデータ（都道府県・学歴カテゴリ・配偶者/年収など）を取得・キャッシュします。

import {
  getEmploymentTypes,
  getGraduationClassifications,
  getIncomes,
  getOccupations,
  getPrefectures,
  getSchoolCategory,
} from '@/apis/candidate';
import { SPECIAL_ITEMS } from '@/constant';
import type { GraduationClassificationType } from '@/types/models/GraduationClassification';
import type { SchoolCategory } from '@/types/models/SchoolCategory';
import type { EmploymentTypePrimary } from '@/types/models/employmentType';
import type { IncomePrimary } from '@/types/models/income';
import type { OccupationPrimary } from '@/types/models/occupation';
import type { PrefecturePrimary } from '@/types/models/prefecture';
import { defineStore } from 'pinia';

type StepStatus = 'idle' | 'loading' | 'loaded' | 'error';

export const useRegistrationMasterDataStore = defineStore('registrationMasterData', {
  state: () => ({
    prefectures: [] as PrefecturePrimary[],
    categories: [] as SchoolCategory[],
    graduationClassifications: [] as GraduationClassificationType[],
    incomes: [] as IncomePrimary[],
    occupations: [] as OccupationPrimary[],
    employmentTypes: [] as EmploymentTypePrimary[],
    step1Status: 'idle' as StepStatus,
    step2Status: 'idle' as StepStatus,
    step3Status: 'idle' as StepStatus,
    step1Promise: null as Promise<void> | null,
    step2Promise: null as Promise<void> | null,
    step3Promise: null as Promise<void> | null,
    step3LoadedOnce: false,
  }),
  actions: {
    /**
     * force=true のときは loaded 済みでも再取得する
     */
    async loadStep1Data(force = false): Promise<void> {
      if (this.step1Status === 'loaded' && !force) return;
      if (this.step1Promise && !force) return this.step1Promise;

      this.step1Status = 'loading';
      this.step1Promise = (async () => {
        try {
          const response = await getPrefectures();
          if (response.status < 200 || response.status >= 300) {
            throw new Error(`prefectures fetch failed (status: ${response.status})`);
          }
          this.prefectures = (response.data as PrefecturePrimary[]).filter(
            prefecture => prefecture.id !== SPECIAL_ITEMS
          );
          this.step1Status = 'loaded';
        } catch (error) {
          this.step1Status = 'error';
          throw error;
        } finally {
          this.step1Promise = null;
        }
      })();

      return this.step1Promise;
    },

    /**
     * force=true のときは loaded 済みでも再取得する
     */
    async loadStep2Data(force = false): Promise<void> {
      if (this.step2Status === 'loaded' && !force) return;
      if (this.step2Promise && !force) return this.step2Promise;

      this.step2Status = 'loading';
      this.step2Promise = (async () => {
        try {
          const [categoriesRes, graduationRes, incomesRes] = await Promise.all([
            getSchoolCategory(),
            getGraduationClassifications(),
            getIncomes(),
          ]);

          if (categoriesRes.status < 200 || categoriesRes.status >= 300) {
            throw new Error(`school categories fetch failed (status: ${categoriesRes.status})`);
          }
          if (graduationRes.status < 200 || graduationRes.status >= 300) {
            throw new Error(
              `graduation classifications fetch failed (status: ${graduationRes.status})`
            );
          }
          if (incomesRes.status < 200 || incomesRes.status >= 300) {
            throw new Error(`incomes fetch failed (status: ${incomesRes.status})`);
          }

          this.categories = categoriesRes.data as SchoolCategory[];
          this.graduationClassifications = graduationRes.data as GraduationClassificationType[];
          this.incomes = incomesRes.data as IncomePrimary[];

          this.step2Status = 'loaded';
        } catch (error) {
          this.step2Status = 'error';
          throw error;
        } finally {
          this.step2Promise = null;
        }
      })();

      return this.step2Promise;
    },

    /**
     * force=true のときは loaded 済みでも再取得する
     */
    async loadStep3Data(force = false): Promise<void> {
      if (this.step3Status === 'loaded' && !force) return;
      if (this.step3Promise && !force) return this.step3Promise;

      this.step3Status = 'loading';
      this.step3Promise = (async () => {
        try {
          const [occupationsRes, employmentTypesRes] = await Promise.all([
            getOccupations(),
            getEmploymentTypes(false),
          ]);

          if (occupationsRes.status < 200 || occupationsRes.status >= 300) {
            throw new Error(`occupations fetch failed (status: ${occupationsRes.status})`);
          }
          if (employmentTypesRes.status < 200 || employmentTypesRes.status >= 300) {
            throw new Error(`employment types fetch failed (status: ${employmentTypesRes.status})`);
          }

          this.occupations = occupationsRes.data as OccupationPrimary[];
          this.employmentTypes = employmentTypesRes.data as EmploymentTypePrimary[];

          this.step3Status = 'loaded';
          this.step3LoadedOnce = true;
        } catch (error) {
          this.step3Status = 'error';
          throw error;
        } finally {
          this.step3Promise = null;
        }
      })();

      return this.step3Promise;
    },
  },
});
